import { useCallback } from 'react';
import { authEvents, planEvents } from '../lib/authEvents';
import { scopeHeaders } from '../lib/scopeHeaders';

/**
 * Hook compartilhado para fetch autenticado.
 *
 * P3 review 2026-09-24: existiam ~13 implementações inline do mesmo helper
 * no projeto (pages, hooks, contexts, components) — risco real de divergência
 * futura (esquecer de adicionar header novo em só um dos call sites). Esta é a
 * implementação canónica. Adicione um novo header aqui, não em outro lugar.
 *
 * Também centraliza a interceptação de 401 que antes era feita via
 * monkey-patch do `window.fetch`. Cada chamada autenticada agora detecta 401,
 * dispara `authEvents.unauthorized()` e o AuthContext faz logout/redirect.
 */
export function useAuthenticatedFetch() {
  return useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...scopeHeaders(),
      ...(options.headers as Record<string, string> | undefined),
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, { ...options, headers });

    // Só vale como sessão inválida o 401 de um token que ainda é o atual: uma chamada feita sem
    // token (tela de login) ou antes de um novo login não pode derrubar a sessão recém-criada.
    if (response.status === 401 && token && localStorage.getItem('auth_token') === token) {
      authEvents.unauthorized(url);
    }
    // Recurso fora do plano ou acesso bloqueado: a janela de upgrade aparece em
    // qualquer tela, sem cada página tratar. O corpo segue legível para quem chamou.
    if (response.status === 402) {
      response.clone().json()
        .then((b: { upgradeRequired?: boolean; code?: string; message?: string; requiredPlan?: string | null; feature?: string }) => {
          if (b?.upgradeRequired && b.message) planEvents.required({ code: b.code, message: b.message, requiredPlan: b.requiredPlan, feature: b.feature });
        })
        .catch(() => undefined);
    }

    return response;
  }, []);
}